import { Entity, Relationship, DomainId, EntityKind } from '../data/schema';
import { entities as graphEntities, relationships as graphRelationships } from '../data/graph';

// Registry-driven data-access API over the entity/relationship graph
// (build-brief §4.2, SAD §2.7). These replace the framework-switching hooks:
// no selector branches on a framework-name literal. Indices give O(1) lookups.
//
// The graph holds the migrated legacy content plus the Phase-2 mapping layer
// (ATT&CK anchor entities + sourced NIST↔ATT&CK `mitigates`), so the mitigation
// and coverage selectors return real, provenance-carrying data.

export interface Graph {
  entities: Entity[];
  relationships: Relationship[];
}

const ENTITIES: Entity[] = graphEntities;
const RELATIONSHIPS: Relationship[] = graphRelationships;

const byId = new Map<string, Entity>(ENTITIES.map((e) => [e.id, e]));
const outgoing = new Map<string, Relationship[]>();
const incoming = new Map<string, Relationship[]>();

function index(map: Map<string, Relationship[]>, key: string, r: Relationship): void {
  const list = map.get(key);
  if (list) list.push(r);
  else map.set(key, [r]);
}
for (const r of RELATIONSHIPS) {
  index(outgoing, r.sourceId, r);
  index(incoming, r.targetId, r);
}

export function getEntity(id: string): Entity | undefined {
  return byId.get(id);
}

export function getEntitiesByDomain(domainId: DomainId, kind?: EntityKind): Entity[] {
  return ENTITIES.filter(
    (e) => e.domains.includes(domainId) && (!kind || e.kind === kind),
  );
}

export function getEntitiesByFramework(frameworkId: string): Entity[] {
  return ENTITIES.filter((e) => e.frameworkId === frameworkId);
}

/** Every relationship touching an entity (incoming + outgoing). Empty array when
 * the entity is unmapped — callers must handle this cleanly (acceptance §4.5). */
export function getRelationshipsFor(entityId: string): Relationship[] {
  return [...(outgoing.get(entityId) ?? []), ...(incoming.get(entityId) ?? [])];
}

/** Controls that mitigate a technique: `control --mitigates--> technique`. */
export function getMitigatingControls(techniqueId: string): Array<{
  control: Entity;
  via: Relationship;
}> {
  return (incoming.get(techniqueId) ?? [])
    .filter((r) => r.type === 'mitigates')
    .map((r) => ({ control: byId.get(r.sourceId), via: r }))
    .filter((x): x is { control: Entity; via: Relationship } => !!x.control);
}

/** Techniques a control covers: `control --mitigates--> technique`. */
export function getTechniquesCoveredBy(controlId: string): Array<{
  technique: Entity;
  via: Relationship;
}> {
  return (outgoing.get(controlId) ?? [])
    .filter((r) => r.type === 'mitigates')
    .map((r) => ({ technique: byId.get(r.targetId), via: r }))
    .filter((x): x is { technique: Entity; via: Relationship } => !!x.technique);
}

/** D3FEND countermeasures that counter a technique: `countermeasure --counters--> technique`. */
export function getCounteringDefenses(techniqueId: string): Array<{
  countermeasure: Entity;
  via: Relationship;
}> {
  return (incoming.get(techniqueId) ?? [])
    .filter((r) => r.type === 'counters')
    .map((r) => ({ countermeasure: byId.get(r.sourceId), via: r }))
    .filter((x): x is { countermeasure: Entity; via: Relationship } => !!x.countermeasure);
}

/** CISA KEV CVEs that enable a technique: `CVE --enables--> technique`. */
export function getKevForTechnique(techniqueId: string): Array<{
  cve: Entity;
  via: Relationship;
}> {
  return (incoming.get(techniqueId) ?? [])
    .filter((r) => r.type === 'enables')
    .map((r) => ({ cve: byId.get(r.sourceId), via: r }))
    .filter((x): x is { cve: Entity; via: Relationship } => !!x.cve);
}

/** Whether a technique is tied to any actively-exploited (KEV) vulnerability. */
export function isKevExploited(techniqueId: string): boolean {
  return (incoming.get(techniqueId) ?? []).some((r) => r.type === 'enables');
}

/** Cross-framework equivalents via `maps-to`, either direction (powers Compare). */
export function getMappedControls(controlId: string): Array<{ entity: Entity; via: Relationship }> {
  return getRelationshipsFor(controlId)
    .filter((r) => r.type === 'maps-to')
    .map((r) => {
      const otherId = r.sourceId === controlId ? r.targetId : r.sourceId;
      return { entity: byId.get(otherId), via: r };
    })
    .filter((x): x is { entity: Entity; via: Relationship } => !!x.entity);
}

/** Free-text search across all entities by id, name and tag — powers the ⌘K
 * palette. Ranks id/name prefix matches above substring matches. */
export function searchEntities(
  query: string,
  opts: { kinds?: EntityKind[]; limit?: number } = {},
): Entity[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const limit = opts.limit ?? 30;
  const score = (e: Entity): number => {
    const id = e.id.toLowerCase();
    const name = e.name.toLowerCase();
    if (id === q) return 5;
    if (id.startsWith(q)) return 4;
    if (name.toLowerCase().startsWith(q)) return 3;
    if (id.includes(q) || name.includes(q)) return 2;
    return 1; // tag-only match
  };
  const matches = ENTITIES.filter((e) => {
    if (opts.kinds && !opts.kinds.includes(e.kind)) return false;
    return (
      e.id.toLowerCase().includes(q) ||
      e.name.toLowerCase().includes(q) ||
      e.tags.some((t) => t.toLowerCase().includes(q))
    );
  });
  return matches.sort((a, b) => score(b) - score(a) || a.id.localeCompare(b.id)).slice(0, limit);
}

/** Defensive blind spots: techniques in a domain with no mitigating control. */
export function getCoverageGaps(domainId: DomainId): Entity[] {
  return getEntitiesByDomain(domainId, 'offensive-technique').filter(
    (t) => getMitigatingControls(t.id).length === 0,
  );
}

export const graph: Graph = { entities: ENTITIES, relationships: RELATIONSHIPS };
